/*
 * Copyright (c) Castle Point Learning Systems Corporation, 2013-2023, all rights reserved.
 * NOTICE: Dissemination of the information and/or materials contained in this file is strictly
 * forbidden unless prior written permission is obtained from Castle Point Learning Systems Corporation.
 * See LICENSE.txt (part of this package) for the full notice.
 */

import React, { createContext, Dispatch, FC, PropsWithChildren, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Evaluation } from '../../../types/model';
import { MathEditor } from 'semantic-math-editor';
import { computeHash } from '../utils/evaluationUtils';

type EvaluationContextType = {
    bindEditor(lineId: string, editor: MathEditor): void;
    unbindEditor(lineId: string): void;
    evaluation: Readonly<Evaluation>;
    dispatchEvaluation: Dispatch<SetStateAction<Evaluation>>;
};

export const EvaluationContext = createContext<EvaluationContextType>({
    bindEditor(): void {},
    unbindEditor(): void {},
    evaluation: null,
    dispatchEvaluation(): void {},
});

interface EvaluationContextProviderProps {
    evaluation: Evaluation;
    dispatchEvaluation: Dispatch<SetStateAction<Evaluation>>;
    onPartUpdate(evaluation: Evaluation): void;
    onStateUpdate(evaluation: Evaluation): void;
}

export const EvaluationContextProvider: FC<PropsWithChildren<EvaluationContextProviderProps>> = ({
    evaluation,
    dispatchEvaluation,
    onPartUpdate,
    onStateUpdate,
    children,
}) => {
    const bindEditor = useCallback(
        (lineId: string, editor: MathEditor) =>
            dispatchEvaluation((prevState) => ({
                ...prevState,
                lines: prevState.lines.map((line) => (lineId === line.id ? { ...line, editor } : line)),
            })),
        [dispatchEvaluation]
    );

    const unbindEditor = useCallback(
        (lineId: string) =>
            dispatchEvaluation((prevState) => ({
                ...prevState,
                lines: prevState.lines.map((line) => (lineId === line.id ? { ...line, editor: undefined } : line)),
            })),
        [dispatchEvaluation]
    );

    const [shouldUpdate, setShouldUpdate] = useState<boolean>(false);
    const hash = useMemo(() => computeHash(evaluation), [evaluation]);

    useEffect(() => {
        onStateUpdate(evaluation);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hash]);

    useEffect(() => {
        if (shouldUpdate && evaluation.dirty) {
            onPartUpdate(evaluation);
            dispatchEvaluation((prevState) => ({ ...prevState, dirty: false }));
        }
        setShouldUpdate(false);
    }, [shouldUpdate, evaluation, onPartUpdate, dispatchEvaluation]);

    useEffect(() => {
        setShouldUpdate(true);
    }, [evaluation.activeLine, evaluation.focused]);

    const context: EvaluationContextType = useMemo(
        () => ({
            bindEditor,
            unbindEditor,
            dispatchEvaluation,
            evaluation,
        }),
        [bindEditor, unbindEditor, evaluation, dispatchEvaluation]
    );

    return <EvaluationContext.Provider value={context}>{children}</EvaluationContext.Provider>;
};
